export var TerrainType;
(function (TerrainType) {
    TerrainType["NONE"] = "none";
    TerrainType["WATER"] = "water";
    TerrainType["PLAINS"] = "plains";
    TerrainType["FOREST"] = "forest";
    TerrainType["HILLS"] = "hills";
    TerrainType["MOUNTAINS"] = "mountains";
    TerrainType["DESERT"] = "desert";
    TerrainType["SWAMP"] = "swamp";
})(TerrainType || (TerrainType = {}));
export var FeatureType;
(function (FeatureType) {
    FeatureType["NONE"] = "none";
    FeatureType["TOWN"] = "town";
    FeatureType["CASTLE"] = "castle";
    FeatureType["RUIN"] = "ruin";
    FeatureType["CAVE"] = "cave";
    FeatureType["TEMPLE"] = "temple";
})(FeatureType || (FeatureType = {}));
// These are intentionally ordered starting at NE and going clockwise
export var HexEdge;
(function (HexEdge) {
    HexEdge[HexEdge["NONE"] = 0] = "NONE";
    HexEdge[HexEdge["NE"] = 1] = "NE";
    HexEdge[HexEdge["E"] = 2] = "E";
    HexEdge[HexEdge["SE"] = 4] = "SE";
    HexEdge[HexEdge["SW"] = 8] = "SW";
    HexEdge[HexEdge["W"] = 16] = "W";
    HexEdge[HexEdge["NW"] = 32] = "NW";
    HexEdge[HexEdge["ALL"] = 63] = "ALL";
})(HexEdge || (HexEdge = {}));
export function terrainToString(terrain) {
    switch (terrain) {
        case TerrainType.WATER: return 'water';
        case TerrainType.PLAINS: return 'plains';
        case TerrainType.FOREST: return 'forest';
        case TerrainType.HILLS: return 'hills';
        case TerrainType.MOUNTAINS: return 'mountains';
        case TerrainType.DESERT: return 'desert';
        case TerrainType.SWAMP: return 'swamp';
        default: return 'unknown terrain';
    }
}
export function featureToString(feature) {
    switch (feature) {
        case FeatureType.TOWN: return 'a town';
        case FeatureType.CASTLE: return 'a castle';
        case FeatureType.RUIN: return 'ruins';
        case FeatureType.CAVE: return 'a cave';
        case FeatureType.TEMPLE: return 'a temple';
        default: return null;
    }
}
export function stringToTerrain(terrainString) {
    if (terrainString) {
        if (Object.values(TerrainType).includes(terrainString)) {
            return terrainString;
        }
    }
    return TerrainType.NONE;
}
export function stringToFeature(featureString) {
    if (featureString) {
        if (Object.values(FeatureType).includes(featureString)) {
            return featureString;
        }
    }
    return FeatureType.NONE;
}
export function stringToHexEdges(edgesString) {
    let hexEdge = HexEdge.NONE;
    if (edgesString) {
        const parts = edgesString.split(',');
        for (const part of parts) {
            const edge = part.trim();
            switch (edge) {
                case 'NW':
                    hexEdge |= HexEdge.NW;
                    break;
                case 'NE':
                    hexEdge |= HexEdge.NE;
                    break;
                case 'SE':
                    hexEdge |= HexEdge.SE;
                    break;
                case 'SW':
                    hexEdge |= HexEdge.SW;
                    break;
                case 'E':
                    hexEdge |= HexEdge.E;
                    break;
                case 'W':
                    hexEdge |= HexEdge.W;
                    break;
                default:
                    console.warn(`Invalid hex edge: ${edge}`);
                    break;
            }
        }
    }
    return hexEdge;
}
export function edgesToArray(edge) {
    const edgeArray = [];
    // Iterate through the enum members (powers of 2)
    for (const key in HexEdge) {
        // Ensure we are dealing with numeric enum values, not string keys
        if (typeof HexEdge[key] === 'number' && HexEdge[key] !== HexEdge.NONE) {
            const flagValue = HexEdge[key];
            // Check if the current flag is set in the provided value
            if ((edge & flagValue) === flagValue) {
                edgeArray.push(flagValue);
            }
        }
    }
    return edgeArray;
}
export function areEdgesAdjacent(edge1, edge2) {
    switch (edge1) {
        case HexEdge.NW:
            return edge2 === HexEdge.NE || edge2 === HexEdge.W;
        case HexEdge.NE:
            return edge2 === HexEdge.NW || edge2 === HexEdge.E;
        case HexEdge.SE:
            return edge2 === HexEdge.E || edge2 === HexEdge.SW;
        case HexEdge.SW:
            return edge2 === HexEdge.W || edge2 === HexEdge.SE;
        case HexEdge.E:
            return edge2 === HexEdge.NE || edge2 === HexEdge.SE;
        case HexEdge.W:
            return edge2 === HexEdge.NW || edge2 === HexEdge.SW;
    }
    return false;
}
export function getOppositeEdge(edge) {
    switch (edge) {
        case HexEdge.NW:
            return HexEdge.SE;
        case HexEdge.NE:
            return HexEdge.SW;
        case HexEdge.SE:
            return HexEdge.NW;
        case HexEdge.SW:
            return HexEdge.NE;
        case HexEdge.E:
            return HexEdge.W;
        case HexEdge.W:
            return HexEdge.E;
    }
    return HexEdge.NONE;
}
export function getAdjacentEdges(edge) {
    switch (edge) {
        case HexEdge.NW:
            return HexEdge.NE | HexEdge.W;
        case HexEdge.NE:
            return HexEdge.NW | HexEdge.E;
        case HexEdge.SE:
            return HexEdge.SW | HexEdge.E;
        case HexEdge.SW:
            return HexEdge.SE | HexEdge.W;
        case HexEdge.E:
            return HexEdge.NE | HexEdge.SE;
        case HexEdge.W:
            return HexEdge.NW | HexEdge.SW;
    }
    return HexEdge.NONE;
}
export function getEdgeBetween(fromX, fromY, toX, toY) {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const key = `${dx},${dy}`;
    const isOddRow = (fromY & 1) === 1;
    if (isOddRow) {
        // odd-r (rows indented to the right)
        switch (key) {
            case '1,0': return HexEdge.E;
            case '-1,0': return HexEdge.W;
            case '1,-1': return HexEdge.NE;
            case '0,-1': return HexEdge.NW;
            case '1,1': return HexEdge.SE;
            case '0,1': return HexEdge.SW;
            default: return HexEdge.NONE;
        }
    }
    else {
        // even-r
        switch (key) {
            case '1,0': return HexEdge.E;
            case '-1,0': return HexEdge.W;
            case '0,-1': return HexEdge.NE;
            case '-1,-1': return HexEdge.NW;
            case '0,1': return HexEdge.SE;
            case '-1,1': return HexEdge.SW;
            default: return HexEdge.NONE;
        }
    }
}
export function edgeToStr(edge) {
    switch (edge) {
        case HexEdge.NW:
            return 'NW';
        case HexEdge.NE:
            return 'NE';
        case HexEdge.SE:
            return 'SE';
        case HexEdge.SW:
            return 'SW';
        case HexEdge.E:
            return 'E';
        case HexEdge.W:
            return 'W';
    }
    return '?';
}
export function edgesToStr(edge) {
    const array = edgesToArray(edge);
    return array.map(e => edgeToStr(e)).join(',');
}
// Used by getAllEdgesBetween to iterate through edges in CW or CCW order
const EDGE_ORDER = [
    HexEdge.NE,
    HexEdge.E,
    HexEdge.SE,
    HexEdge.SW,
    HexEdge.W,
    HexEdge.NW,
];
export function isSingleEdge(edge) {
    return edge !== HexEdge.NONE && (edge & (edge - 1)) === 0;
}
export function getAllEdgesBetween(from, to, clockwise) {
    if (!isSingleEdge(from) || !isSingleEdge(to)) {
        throw new Error("from and to must be single non-NONE HexEdge values.");
    }
    const len = EDGE_ORDER.length;
    const fromIdx = EDGE_ORDER.indexOf(from);
    const toIdx = EDGE_ORDER.indexOf(to);
    if (fromIdx === -1 || toIdx === -1) {
        throw new Error("Invalid HexEdge value.");
    }
    // Degenerate: same edge -> just that edge
    if (fromIdx === toIdx) {
        return from;
    }
    let mask = HexEdge.NONE;
    let idx = fromIdx;
    while (true) {
        if (clockwise) {
            idx = (idx + 1) % len;
        }
        else {
            idx = (idx - 1 + len) % len;
        }
        if (idx === toIdx)
            break;
        mask |= EDGE_ORDER[idx];
    }
    return mask;
}
